<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import FieldValue from './FieldValue.vue';
import ConditionValue from './ConditionValue.vue';
import ConditionParameter from './ConditionParameter.vue';
import { conditionField, conditionMessages } from './conditions';
import en from './en.json';
import ru from './ru.json';

const props = defineProps({
  catalog: { type: Object, required: true },
  event: { type: String, required: true },
  connectionId: { type: Number, default: null },
});
const model = defineModel({ type: Object, required: true });
const { t } = useI18n({
  useScope: 'local',
  messages: {
    en: { ...en, ...conditionMessages.en },
    ru: { ...ru, ...conditionMessages.ru },
  },
});
const context = computed(() => model.value.context || 'appointment');
const hasParameter = computed(() => model.value.rules.some(rule => rule.field));
const sources = computed(() => {
  if (context.value !== 'appointment') return [context.value];
  return (props.catalog.sources || []).filter(
    source =>
      source !== 'computed' &&
      (source !== 'previous' || ['updated', 'deleted'].includes(props.event))
  );
});
const fieldFor = rule => conditionField(props.catalog, rule);
const operatorsFor = rule => {
  const field = fieldFor(rule);
  const numeric = ['int4', 'int8', 'float8'].includes(field.type);
  const result = (props.catalog.operators || []).filter(op => {
    if (op === 'changed')
      return props.event === 'updated' && rule.source === 'event';
    if (field.references || field.options)
      return ['eq', 'neq', 'present', 'blank'].includes(op);
    if (op === 'contains') return ['text', '_text'].includes(field.type);
    if (['gt', 'gte', 'lt', 'lte'].includes(op))
      return numeric || field.type === 'timestamp';
    return true;
  });
  if (numeric) result.push('between');
  if (field.is_date)
    result.push(...(props.catalog.condition_date_operators || []));
  return result;
};
const noValue = op => ['present', 'blank', 'changed', 'today'].includes(op);
const relative = op => op.startsWith('future_') || op.startsWith('past_');
const range = op =>
  ['between', 'date_between', 'future_between', 'past_between'].includes(op);
const calendar = op =>
  ['on_date', 'before_date', 'after_date', 'date_between'].includes(op);
const changeOperator = rule => {
  delete rule.reference_mis_number;
  delete rule.value_label;
  if (relative(rule.op))
    rule.value = range(rule.op)
      ? { from: null, to: null, unit: 'days' }
      : { amount: null, unit: 'days' };
  else rule.value = range(rule.op) ? { from: null, to: null } : null;
};
const changeField = rule => {
  if (rule.field.startsWith('minutes_')) rule.source = 'computed';
  else if (rule.source === 'computed') rule.source = context.value;
  rule.op = fieldFor(rule).is_date ? 'on_date' : 'eq';
  rule.value = null;
  delete rule.reference_mis_number;
  delete rule.value_label;
};
const changeSource = rule => {
  if (
    !(props.catalog.condition_fields?.[context.value] || []).some(
      field =>
        field.name === rule.field &&
        field.name.startsWith('minutes_') === (rule.source === 'computed')
    )
  )
    rule.field = '';
  changeField(rule);
};
const selectParameter = (index, { context: next, field }) => {
  const switching = next !== context.value;
  // eslint-disable-next-line no-alert
  if (
    switching &&
    hasParameter.value &&
    !window.confirm(t('conditionEditor.changeContext'))
  )
    return;
  const previous = switching ? null : model.value.rules[index];
  if (previous?.field === field.name) return;
  let source = previous?.source || next;
  if (field.name.startsWith('minutes_')) source = 'computed';
  else if (source === 'computed') source = next;
  const rule = {
    source,
    field: field.name,
    op: field.is_date ? 'on_date' : 'eq',
    value: null,
  };
  if (switching) {
    model.value = {
      context: next,
      match: 'all',
      rules: [rule],
      ...(next === 'appointment_service' ? { collection_match: 'any' } : {}),
    };
  } else {
    model.value.rules.splice(index, 1, rule);
  }
};
const addRule = () =>
  model.value.rules.push({
    source: context.value,
    field: '',
    op: 'eq',
    value: null,
  });
const setNumber = (rule, key, value) => {
  if (value === '') rule.value[key] = null;
  else
    rule.value[key] =
      rule.op === 'between' && fieldFor(rule).type === 'int8'
        ? String(value)
        : Number(value);
};
</script>

<template>
  <div class="space-y-4">
    <ConditionParameter
      v-if="!model.rules.length"
      :catalog="catalog"
      :context="context"
      @select="selectParameter(0, $event)"
    />
    <p v-if="hasParameter" class="text-xs text-n-slate-11">
      {{ t(`conditionEditor.${context}Hint`) }}
    </p>
    <label
      v-if="hasParameter && context === 'appointment_service'"
      class="block text-sm font-medium text-n-slate-12"
    >
      {{ t('conditionEditor.collection') }}
      <select
        :value="model.collection_match || 'any'"
        class="w-full mt-2 text-sm"
        @change="model.collection_match = $event.target.value"
      >
        <option value="any">{{ t('conditionEditor.anyProduct') }}</option>
        <option value="none">{{ t('conditionEditor.noProduct') }}</option>
      </select>
    </label>
    <select
      v-if="model.rules.length > 1"
      v-model="model.match"
      :aria-label="t('condition')"
      class="w-full text-sm"
    >
      <option value="all">{{ t('all') }}</option>
      <option value="any">{{ t('any') }}</option>
    </select>
    <div
      v-for="(rule, index) in model.rules"
      :key="index"
      class="grid grid-cols-1 sm:grid-cols-2 items-start gap-3 py-4 border-t border-n-weak"
    >
      <div
        v-if="model.rules.length > 1"
        class="flex items-center justify-between sm:col-span-2"
      >
        <span class="text-xs text-n-slate-11">{{ index + 1 }}</span>
        <Button
          :label="t('remove')"
          color="slate"
          variant="ghost"
          size="sm"
          type="button"
          @click="model.rules.splice(index, 1)"
        />
      </div>
      <ConditionParameter
        class="sm:col-span-2"
        :catalog="catalog"
        :context="context"
        :field="rule.field"
        @select="selectParameter(index, $event)"
      />
      <label v-if="rule.field" class="block text-xs text-n-slate-11">
        {{ t('condition') }}
        <select
          v-model="rule.op"
          class="w-full mt-1 text-sm"
          @change="changeOperator(rule)"
        >
          <option v-for="op in operatorsFor(rule)" :key="op" :value="op">
            {{ t(op) }}
          </option>
        </select>
      </label>
      <template v-if="rule.field && !noValue(rule.op)">
        <div v-if="range(rule.op)" class="grid grid-cols-2 gap-2">
          <Input
            v-for="key in ['from', 'to']"
            :key="key"
            :model-value="rule.value?.[key] ?? ''"
            :type="
              calendar(rule.op)
                ? 'date'
                : rule.op === 'between' && fieldFor(rule).type === 'int8'
                  ? 'text'
                  : 'number'
            "
            :label="t(`conditionEditor.${key}`)"
            :step="calendar(rule.op) ? undefined : 'any'"
            @update:model-value="
              calendar(rule.op)
                ? (rule.value[key] = $event)
                : setNumber(rule, key, $event)
            "
          />
        </div>
        <Input
          v-else-if="relative(rule.op)"
          :model-value="rule.value?.amount ?? ''"
          type="number"
          :label="t('amount')"
          step="any"
          @update:model-value="setNumber(rule, 'amount', $event)"
        />
        <Input
          v-else-if="calendar(rule.op)"
          v-model="rule.value"
          type="date"
          :label="t('value')"
        />
        <label
          v-else-if="fieldFor(rule).options"
          class="block text-xs text-n-slate-11"
        >
          {{ t('value') }}
          <select v-model="rule.value" class="w-full mt-1 text-sm">
            <option :value="null">{{ t('choose') }}</option>
            <option
              v-for="option in fieldFor(rule).options"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>
        <ConditionValue
          v-else-if="
            fieldFor(rule).editor_visible &&
            (fieldFor(rule).references || fieldFor(rule).type === 'text')
          "
          v-model:rule="model.rules[index]"
          :field="fieldFor(rule)"
          :context="context"
          :connection-id="connectionId"
        />
        <p
          v-else-if="fieldFor(rule).editor_visible === false"
          class="text-xs text-n-slate-11"
        >
          {{ t('conditionEditor.legacyValue') }}
        </p>
        <label v-else class="block text-xs text-n-slate-11">
          {{ t('value') }}
          <FieldValue
            v-model="rule.value"
            class="mt-1"
            :field="fieldFor(rule)"
            :contains="rule.op === 'contains'"
            :label="t('value')"
          />
        </label>
        <select
          v-if="relative(rule.op)"
          v-model="rule.value.unit"
          :aria-label="t('amount')"
          class="w-full text-sm"
        >
          <option v-for="unit in catalog.units" :key="unit" :value="unit">
            {{ t(unit) }}
          </option>
        </select>
      </template>
      <details
        v-if="
          rule.field && context === 'appointment' && rule.source !== 'computed'
        "
        class="sm:col-span-2 text-xs text-n-slate-11"
      >
        <summary class="cursor-pointer">
          {{ t('conditionEditor.source') }}:
          {{ t(rule.source === 'event' ? 'eventSource' : rule.source) }}
        </summary>
        <select
          v-model="rule.source"
          :aria-label="t('conditionEditor.source')"
          class="w-full mt-2 text-sm"
          @change="changeSource(rule)"
        >
          <option v-for="source in sources" :key="source" :value="source">
            {{ t(source === 'event' ? 'eventSource' : source) }}
          </option>
          <option
            v-if="!sources.includes(rule.source)"
            :value="rule.source"
            disabled
          >
            {{ t('previous_context_unavailable') }}
          </option>
        </select>
      </details>
      <p
        v-if="fieldFor(rule).is_date"
        class="sm:col-span-2 text-xs text-n-slate-11"
      >
        {{ t('conditionEditor.timezone') }}
      </p>
      <p v-if="relative(rule.op)" class="sm:col-span-2 text-xs text-n-slate-11">
        {{ t('conditionEditor.relativeHint') }}
      </p>
    </div>
    <Button
      v-if="hasParameter"
      :label="t('addRule')"
      icon="i-lucide-plus"
      color="slate"
      variant="ghost"
      size="sm"
      type="button"
      @click="addRule"
    />
  </div>
</template>
